import * as React from "react";
import { LoaderCircle } from "lucide-react";
import { useNavigate } from "react-router";

import { useGetActiveContractFamilyPipelineRunsQuery } from "@/api/contract-family-pipeline";
import { Button } from "@/components/ui/button";
import { buildContractFamiliesPath } from "../navigation";

export const ActiveFamilyWorkflows: React.FC<{ folderId: string | null }> = ({ folderId }) => {
  const navigate = useNavigate();
  // Poll fast only while runs are in flight; an empty list is re-checked every 30 s so a run started
  // elsewhere (another tab, automation) still shows up without hammering the endpoint.
  const [hasRuns, setHasRuns] = React.useState(false);
  const { data, isLoading } = useGetActiveContractFamilyPipelineRunsQuery(folderId, {
    pollingInterval: hasRuns ? 5000 : 30000,
    skipPollingIfUnfocused: true,
  });
  React.useEffect(() => {
    setHasRuns(Boolean(data?.runs.length));
  }, [data?.runs.length]);
  if (isLoading)
    return (
      <div className="flex items-center gap-2 border-b bg-white px-4 py-3 text-sm text-slate-600">
        <LoaderCircle className="h-4 w-4 animate-spin" /> Loading workflows…
      </div>
    );
  if (!data?.runs.length) return null;
  return (
    <section aria-label="Workflows in progress" className="border-b bg-white px-4 py-3">
      <h2 className="mb-2 text-sm font-semibold">Workflows in progress</h2>
      <div className="grid gap-2">
        {data.runs.map(run => (
          <div key={run.id} className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <p className="text-sm font-medium">{run.original_collection_name}</p>
              <p className="text-xs capitalize text-slate-500">{run.stage.replaceAll("_", " ")}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                navigate(
                  buildContractFamiliesPath(run.source_folder_fse_id, {
                    pipelineRunId: run.id,
                    snapshotCollectionId: run.original_collection_id ?? undefined,
                  }),
                )
              }
            >
              Continue
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
};
